// src/components/Contact.js
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SupportLanguageNotice from './SupportLanguageNotice';
import { LEGAL_DETAILS } from '../config/legal';
import { useI18n } from '../i18n/I18nContext';
import { contactCopy, pickPageCopy } from '../i18n/pageCopy';
import { getRecaptchaToken, isRecaptchaConfigured } from '../services/recaptcha';
import { sendContactRequest } from '../services/contactSupport';
import '../style/Homepage.css';
import '../style/Contact.css';

const validTopics = new Set([
  'booking',
  'payment',
  'room',
  'privacy',
  'safety',
  'reader',
  'partnership',
  'press',
]);

const Contact = () => {
  const { language } = useI18n();
  const copy = pickPageCopy(language, contactCopy);
  const [searchParams] = useSearchParams();
  const initialTopic = validTopics.has(searchParams.get('topic'))
    ? searchParams.get('topic')
    : 'booking';
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topic, setTopic] = useState(initialTopic);

  const responseTips = useMemo(
    () => {
      if (topic === 'partnership') return copy.partnershipTips;
      if (topic === 'privacy') return copy.privacyTips;
      return copy.tips;
    },
    [copy, topic],
  );

  const messagePlaceholder = copy.placeholders?.[topic] || copy.placeholders?.booking || contactCopy.en.placeholders.booking;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    const formData = new FormData(event.currentTarget);
    const entries = Object.fromEntries(formData.entries());
    const payload = {
      name: String(entries.name || '').trim(),
      email: String(entries.email || '').trim(),
      topic: String(entries.topic || topic),
      priority: String(entries.priority || 'normal'),
      bookingId: String(entries.bookingId || '').trim(),
      sessionDate: String(entries.sessionDate || ''),
      message: String(entries.message || '').trim(),
    };

    if (isRecaptchaConfigured()) {
      setIsSubmitting(true);
      try {
        const recaptchaToken = await getRecaptchaToken('contact_request');
        await sendContactRequest({ ...payload, recaptchaToken });
        setSubmitted(true);
        return;
      } catch (error) {
        setSubmitError(error?.message || 'We could not send your request. Please try again.');
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    const subject = `Fate Room ${entries.topic || topic} request - ${entries.name || 'User'}`;
    const body = [
      `Name: ${entries.name || ''}`,
      `Account email: ${entries.email || ''}`,
      `Topic: ${entries.topic || topic}`,
      `Priority: ${entries.priority || 'normal'}`,
      `Booking ID: ${entries.bookingId || 'Not provided'}`,
      `Session date: ${entries.sessionDate || 'Not provided'}`,
      '',
      'Message:',
      entries.message || '',
    ].join('\n');
    const recipient = topic === 'privacy'
      ? LEGAL_DETAILS.privacyEmail
      : LEGAL_DETAILS.supportEmail;

    window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <div>
      <Header />
      <main className="info-page contact-page redesigned-contact-page">
        <section className="contact-hero-card">
          <div>
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1>{copy.title}</h1>
            <p>{copy.body}</p>
          </div>
          <div className="contact-mini-dashboard" aria-label={copy.overview}>
            <span>{copy.desk}</span>
            <strong>24-48h</strong>
            <p>{copy.response}</p>
            <div className="contact-status-line"><i /><i /><i /></div>
          </div>
        </section>

        <SupportLanguageNotice />

        <section className="contact-routing-grid" aria-label={copy.categories}>
          {copy.routes.map(([title, body]) => (
            <article className="contact-route-card" key={title}>
              <span>{title.slice(0, 1)}</span>
              <h2>{title}</h2>
              <p>{body}</p>
            </article>
          ))}
        </section>

        <section className="contact-workspace">
          <aside className="contact-aside-card">
            <p className="eyebrow">{copy.before}</p>
            <h2>{topic === 'partnership' ? copy.partnershipReview : copy.details}</h2>
            <ul>
              {responseTips.map((tip) => <li key={tip}>{tip}</li>)}
            </ul>
            <div className="contact-priority-card">
              <strong>
                {topic === 'partnership'
                  ? copy.notPartnership
                  : topic === 'privacy'
                    ? copy.googleRequest
                    : copy.safetyIssue}
              </strong>
              <p>
                {topic === 'partnership' ? (
                  <>
                    {copy.partnershipHelp}
                  </>
                ) : topic === 'privacy' ? (
                  <>
                    <Link to="/account-and-google-privacy">{copy.privacyLink}</Link>. {copy.privacyBefore}
                  </>
                ) : (
                  <>
                    {copy.safetyHelp}
                  </>
                )}
              </p>
            </div>
          </aside>

          <section className="contact-panel modern-contact-panel">
            {submitted ? (
              <div className="success-box modern-success-box">
                <span>{copy.draft}</span>
                <h2>{copy.reviewEmail}</h2>
                <p>{copy.draftBody}</p>
                <button className="secondary-button" type="button" onClick={() => setSubmitted(false)}>
                  {copy.another}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="modern-contact-form">
                <div className="contact-form-row">
                  <label>{copy.fullName}<input type="text" name="name" placeholder={copy.namePlaceholder} required /></label>
                  <label>{copy.accountEmail}<input type="email" name="email" placeholder="you@example.com" required /></label>
                </div>

                <div className="contact-form-row">
                  <label>
                    {copy.topic}
                    <select
                      name="topic"
                      value={topic}
                      onChange={(event) => setTopic(event.target.value)}
                    >
                      {Object.entries(copy.topics).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label>
                    {copy.priority}
                    <select name="priority" defaultValue="normal">
                      <option value="normal">{copy.normal}</option>
                      <option value="booking_today">{copy.today}</option>
                      <option value="payment_pending">{copy.pending}</option>
                      <option value="safety">{copy.concern}</option>
                    </select>
                  </label>
                </div>

                <div className="contact-form-row">
                  <label>{copy.bookingId}<input type="text" name="bookingId" placeholder={copy.bookingExample} /></label>
                  <label>{copy.sessionDate}<input type="date" name="sessionDate" /></label>
                </div>

                <label>
                  {topic === 'partnership' ? copy.proposal : copy.need}
                  <textarea
                    name="message"
                    rows="7"
                    placeholder={messagePlaceholder}
                    required
                  />
                </label>

                <div className="contact-consent-box">
                  <label>
                    <input type="checkbox" required />
                    {copy.consent}
                  </label>
                </div>

                <button type="submit" className="primary-button contact-submit-button">
                  {isSubmitting ? 'Sending securely...' : topic === 'partnership' ? copy.sendPartnership : copy.sendSupport}
                </button>
                {submitError && <p className="form-error" role="alert">{submitError}</p>}
              </form>
            )}
          </section>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
